import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Paperclip, Receipt } from "lucide-react";
import { api, formatApiError } from "../../../api";
import { DESK } from "../../../constants/testIds";
import { useDeskData } from "../../../contexts/DeskDataContext";
import { formatCurrency } from "../../../lib/format";
import { fadeUp } from "../../../lib/motion";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { toast } from "../../../components/ui/sonner";
import { FormField } from "../../../components/common/FormField";
import { SubmitButton } from "../../../components/common/SubmitButton";
import { useSuccessPulse } from "../../../hooks/useSuccessPulse";

const CATEGORIES = ["rent", "utility", "salary", "equipment", "maintenance", "housekeeping", "marketing", "software", "misc"];

// UX-FD-03 §1 SCR-P02-81: unlike FD's petty-cash-only `expense.manage_petty`, a Manager holds
// full `expense.manage` — every category + any paid_from is genuinely unrestricted server-side
// (`routers/expense.py`'s `petty_tier_only` check only fires for the petty-only permission tier).
// Feeds the branch P&L directly — verified end-to-end via the Dashboard tab's own `/reports/pnl`.
export default function Expenses() {
  const { branchId, readOnlyLapse } = useDeskData();
  const reducedMotion = useReducedMotion();
  const queryClient = useQueryClient();
  const [category, setCategory] = useState("rent");
  const [amount, setAmount] = useState("");
  const [paidFrom, setPaidFrom] = useState("bank");
  const [note, setNote] = useState("");
  const [file, setFile] = useState(null);
  const [success, pulse] = useSuccessPulse();

  const listQ = useQuery({
    queryKey: ["desk-mgr-expenses", branchId],
    queryFn: async () => (await api.get("/expenses", { params: { branch_id: branchId } })).data.expenses,
    enabled: !!branchId,
  });

  const submitMutation = useMutation({
    mutationFn: async () => {
      let attachment_key = null;
      if (file) {
        const form = new FormData();
        form.append("file", file);
        attachment_key = (await api.post("/expenses/attachments", form)).data.attachment_key;
      }
      return api.post(
        "/expenses",
        { branch_id: branchId, category, amount: Math.round(Number(amount) * 100), paid_from: paidFrom, note: note || null, attachment_key },
        { headers: { "Idempotency-Key": crypto.randomUUID() } },
      );
    },
    onSuccess: () => pulse(() => {
      toast.success("Expense recorded — feeds the branch P&L immediately.");
      setAmount(""); setNote(""); setFile(null);
      queryClient.invalidateQueries({ queryKey: ["desk-mgr-expenses"] });
      queryClient.invalidateQueries({ queryKey: ["desk-mgr-pnl"] });
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  return (
    <div data-testid={DESK.mgrExpensePage} className="space-y-6 max-w-2xl">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Expenses</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Record any category — rent, salary, utilities, marketing and more. Feeds the branch P&L directly.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <div className="grid grid-cols-2 gap-3">
          <Select onValueChange={setCategory} value={category}>
            <SelectTrigger data-testid={DESK.mgrExpenseCategorySelect}><SelectValue /></SelectTrigger>
            <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</SelectItem>)}</SelectContent>
          </Select>
          <Select onValueChange={setPaidFrom} value={paidFrom}>
            <SelectTrigger data-testid={DESK.mgrExpensePaidFromSelect}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cash">Cash</SelectItem>
              <SelectItem value="bank">Bank</SelectItem>
              <SelectItem value="digital">Digital</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <FormField required type="number" testId={DESK.mgrExpenseAmountInput} placeholder="Amount (₹) *" value={amount} onChange={setAmount} />
        <FormField testId={DESK.mgrExpenseNoteInput} placeholder="Note (optional)" value={note} onChange={setNote} />
        <label className="flex items-center gap-2 text-sm cursor-pointer" style={{ color: "#68686E" }}>
          <Paperclip size={14} />
          {file ? file.name : "Attach bill photo (optional)"}
          <input data-testid={DESK.mgrExpenseAttachmentInput} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <SubmitButton
          testId={DESK.mgrExpenseSubmitButton}
          disabled={!amount || readOnlyLapse}
          pending={submitMutation.isPending} success={success}
          onClick={() => submitMutation.mutate()}
          fullWidth label="Record expense" successLabel="Recorded"
        />
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.08)} className="rounded-[14px] overflow-hidden" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
        <p className="flex items-center gap-1.5 text-sm font-semibold px-4 py-3" style={{ color: "#141416", borderBottom: "1px solid #EBEAE5" }}><Receipt size={15} /> Recent expenses</p>
        {listQ.data?.length === 0 && <p className="p-4 text-sm" style={{ color: "#68686E" }}>No expenses recorded yet.</p>}
        {listQ.data?.map((e) => (
          <div key={e.id} data-testid={DESK.mgrExpenseRow(e.id)} className="flex items-center justify-between px-4 py-2.5 text-sm" style={{ borderBottom: "1px solid #F4F3EF" }}>
            <div>
              <span className="font-medium" style={{ color: "#141416" }}>{e.category}</span>
              <span className="mx-1.5" style={{ color: "#D9D6CE" }}>·</span>
              <span style={{ color: "#68686E" }}>{e.paid_from}</span>
              {e.note && <span className="ml-1.5" style={{ color: "#68686E" }}>— {e.note}</span>}
            </div>
            <span className="font-semibold" style={{ color: "#141416" }}>{formatCurrency(e.amount)}</span>
          </div>
        ))}
      </motion.div>
    </div>
  );
}
